import HTMLTemplate from './template.html'
import './styles.scss'
import MapBox from '../../modules/map'
import { highlightCountry, unhighlightCountry } from '../../services/airport'
import moment from 'moment'
import 'moment-timezone'
import { COUNTRY_DETAILS } from '../../data/country-details'
import Airport from '../../modules/airport'
import { convertDMS } from '../../services/utils'
import User from '../../modules/user'
import { unhighlightHotspot } from '../../services/EventsService'
import poweredbyRIImage from './../../resources/images/luxury-yachting/poweredbyRI.png'
import RiskIntelligenceAPIService from './../../services/api/RiskIntelligence/ApiService'
import ClientPortalAPIService from './../../services/api/ClientPortal/ApiService'
import Sentry from './../../modules/SantryIO'
import MixPanelService from '../../services/MixPanelService'

export const EventDetailsPanel = (() => {
    const guessedTimeZone = moment.tz(moment.tz.guess()).format('z')
    const vueComponent = {
        data() {
            return {
                currentActiveEvent: '', // international-sos-alert, avsec-alert
                isPanelOpen: false,
                internationalSOSAlertDetails: {
                    status: '', // Live, Archived, default value: Live
                    createdDate: '',
                    severity: '',
                    title: '',
                    titleLocation: '',
                    categories: '',
                    activeFrom: '',
                    activeTo: '',
                    eventStart: '',
                    eventEnd: '',
                    summary: '',
                    travellerAdvice: '',
                    moreDetail: '',
                    lngLat: ''
                },
                aviationAlerts: [],
                aviationAlertType: '',
                aviationAlertAffectedLocationDetails: {
                    displayCoordinates: '',
                    displayRadius: ''
                },
                aviationAlertAffectedAirportDetails: '',
                hotspotDetails: {
                    id: '',
                    time: '',
                    headline: '',
                    location: '',
                    summary: '',
                    threatLevel: '',
                    type: '',
                    threatAssessment: {
                        threats: [],
                        summary: ''
                    },
                    isLoading: false
                },
                maritimeAlertDetails: {
                    id: '',
                    createdDate: '',
                    updatedDate: '',
                    summary: '',
                    region: '',
                    country: '',
                    title: '',
                    teaser: '',
                    categories: '',
                    maritimeEventImage: '',
                    lngLat: '',
                    convertedLatlng: '',
                    alertTypeId: ''
                },
                showImageLightbox: false,
                isMaritimeImageLoaded: false,
                poweredbyRIImage: poweredbyRIImage,
                isExportingHotspotDetails: false,
                newsAlertDetails: {}
            }
        },
        mounted() {
            window.addEventListener('resize', this.updateAccordionHeights)
        },
        unmounted() {
            window.addEventListener('resize', this.updateAccordionHeights)
        },
        methods: {
            onMaritimeImageLoad() {
                this.isMaritimeImageLoaded = true
            },
            showInternationalSOSAlert(alertDetails, lngLat) {
                this.internationalSOSAlertDetails = {
                    status: alertDetails.Status,
                    createdDate: alertDetails.CreatedDate !== '' ? this.getEventFormattedDate(alertDetails.CreatedDate) : '',
                    severity: alertDetails.Severity,
                    title: alertDetails.Title,
                    titleLocation: alertDetails.TitleLocation,
                    categories: alertDetails.Categories,
                    activeFrom: this.getEventSimplifiedDate(alertDetails.ActiveFrom),
                    activeTo: this.getEventSimplifiedDate(alertDetails.ActiveTo),
                    eventStart: null,
                    eventEnd: null,
                    summary: alertDetails.Summary,
                    travellerAdvice: alertDetails.TravellerAdvice,
                    moreDetail: alertDetails.MoreDetail,
                    lngLat: lngLat ?? alertDetails.Locations.features[0].geometry.coordinates,
                    service: alertDetails.Service
                }

                if (alertDetails.EventStart !== null) {
                    this.internationalSOSAlertDetails.eventStart = this.getEventSimplifiedDate(alertDetails.EventStart)
                }

                if (alertDetails.EventEnd !== null) {
                    this.internationalSOSAlertDetails.eventEnd = this.getEventSimplifiedDate(alertDetails.EventEnd)
                }

                this.currentActiveEvent = 'international-sos-alert'

                this.showPanel()
                this.triggerOpeningEffect(this.internationalSOSAlertDetails.lngLat)
            },
            showNewsAlert(newsAlert) {
                const alertDate = newsAlert.publishedAt
                const isEnglish = newsAlert.translations ? false : true
                const title = isEnglish ? newsAlert.title : newsAlert.translations?.en?.title
                const summary = isEnglish ? newsAlert.summary.sentences.join(' ') : newsAlert.body
                
                this.newsAlertDetails = {
                    id: newsAlert.id,
                    date: this.getEventFormattedDate(alertDate),
                    title: title,
                    summary: summary,
                    sourceName : newsAlert.source.name,
                    link : newsAlert?.links?.permalink
                }
                this.currentActiveEvent = 'news-alert'
                this.showPanel()
            },
            showAviationCountryAlerts(avsecAlerts, countryCodeAlpha2ToHighlight, isHighightCountry = true) {
                this.aviationAlerts = avsecAlerts
                this.aviationAlertType = 'country'

                this.currentActiveEvent = 'avsec-alert'
                this.showPanel()

                MixPanelService.track('Avsec Alert Clicked', { 
                    AlertCount: avsecAlerts?.length,
                    Type: 'Country Affected',
                    CountryCode: countryCodeAlpha2ToHighlight || ''
                })

                if (countryCodeAlpha2ToHighlight !== undefined) {
                    const countryDetails = COUNTRY_DETAILS.find(details => details.ISO_3166_1_ALPHA_2 === countryCodeAlpha2ToHighlight)
                    this.triggerOpeningEffect([countryDetails.centralLng, countryDetails.centralLat], 4)

                    if (isHighightCountry) {
                        const isBreakingAlert = avsecAlerts.filter(alert => alert.IsBreaking)
                        let fillColor = ""
                        let outLineColor = ""
                        if (isBreakingAlert.length === avsecAlerts.length) {
                            fillColor = "rgba(237, 130, 39, 0.1)"
                            outLineColor = "rgba(237, 130, 39, 1)"
                        } else {
                            fillColor = "rgba(212, 67, 51, 0.1)"
                            outLineColor = "rgba(212, 67, 51, 1)"
                        }
                        highlightCountry(countryCodeAlpha2ToHighlight, countryDetails.centralLng, countryDetails.centralLat, {
                            fillColor: fillColor,
                            outLineColor: outLineColor
                        })
                    }
                }
            },
            showMaritimeAlert(alertDetails) {
                this.isMaritimeImageLoaded = false
                this.maritimeAlertDetails.maritimeEventImag = ''
                const countryName = alertDetails?.entity?.name ?? ''
                const areaName = alertDetails?.area?.name ?? ''
                const regionName = alertDetails?.region?.name ?? ''
                const names = [regionName, areaName, countryName].filter(name => name !== '')
                const location = names.join(', ')
                this.maritimeAlertDetails = {
                    id: alertDetails.id,
                    createdDate: alertDetails.date !== '' ? this.getEventFormattedDate(alertDetails.date) : '',
                    updatedDate: alertDetails.updated !== '' ? this.getEventSimplifiedDateWithoutTimeZone(alertDetails.updated) : '',
                    summary: alertDetails.description,
                    region: alertDetails.region.name,
                    country: alertDetails?.entity?.name,
                    location: location,
                    title: alertDetails.title,
                    teaser: alertDetails.teaser,
                    categories: [alertDetails.type.description],
                    lngLat: [alertDetails.markerPosition.longitude, alertDetails.markerPosition.latitude],
                    convertedLatlng: convertDMS(alertDetails.markerPosition.latitude, alertDetails.markerPosition.longitude),
                    alertTypeId: alertDetails.type.id
                }
                this.currentActiveEvent = 'maritime-alert'
                this.showPanel()
                this.triggerOpeningEffect(this.maritimeAlertDetails.lngLat)
                setTimeout(() => {
                    this.maritimeAlertDetails.maritimeEventImage = (alertDetails.images && alertDetails.images.length > 0) ? alertDetails.images[0].url : ''
                }, 200)
            },
            showAvsecAlerts(avsecAlerts, countryCodeAlpha2ToHighlight) {
                this.avsecAlerts = avsecAlerts
                this.currentActiveEvent = 'avsec-alert'

                this.showPanel()

                if (countryCodeAlpha2ToHighlight !== undefined) {
                    const countryDetails = COUNTRY_DETAILS.find(details => details.ISO_3166_1_ALPHA_2 === countryCodeAlpha2ToHighlight)
                    this.triggerOpeningEffect([countryDetails.centralLng, countryDetails.centralLat], 4)
                    highlightCountry(countryCodeAlpha2ToHighlight, countryDetails.centralLng, countryDetails.centralLat)
                }
            },
            showAviationAlert(avsecAlert, type) {
                this.aviationAlerts = [avsecAlert]
                this.aviationAlertType = type

                this.currentActiveEvent = 'avsec-alert'
                this.showPanel()

                // When an alert is clicked we just open it, but for some alert types we cant to handle the display differently
                if (type === 'location') {
                    let latLngData = null
                    if (avsecAlert.AffectedLocation?.latlng) {
                        latLngData = avsecAlert.AffectedLocation?.latlng
                    } else {
                        if (avsecAlert.AffectedLocation?.Coordinates?.length > 0) {
                            latLngData = {
                                lat: avsecAlert.AffectedLocation?.Coordinates[1],
                                lng: avsecAlert.AffectedLocation?.Coordinates[0]
                            }
                        }
                    }

                    let distance = 0
                    if (avsecAlert?.AffectedLocation?.Radius) {
                        distance = User.userSettings.mesaurementUnits === 'Metric' ?
                            `${avsecAlert.AffectedLocation.Radius} km` :
                            `${Math.round(avsecAlert.AffectedLocation.Radius * 0.621371)} mi`
                    }

                    this.aviationAlertAffectedLocationDetails = {
                        displayCoordinates: latLngData ? convertDMS(latLngData.lat, latLngData.lng) : "",
                        displayRadius: (avsecAlert?.AffectedLocation?.Radius > 0 && distance) ? `${distance} Radius` : '' // In case there is no radius, we don't want to display it
                    }

                    if (latLngData) {
                        this.triggerOpeningEffect({ lng: latLngData.lng, lat: latLngData.lat })
                    } else {
                        this.triggerOpeningEffect()
                    }
                } else {
                    this.triggerOpeningEffect()
                }
            },
            showAviationAirportAlert(avsecAlert, airportDetails) {
                this.aviationAlerts = [avsecAlert]
                this.aviationAlertType = 'airport'
                this.aviationAlertAffectedAirportDetails = `${airportDetails.Name} Airport (${Airport.getUserPreferredAirportCode(airportDetails.ICAO)})`

                this.currentActiveEvent = 'avsec-alert'
                this.showPanel()

                this.triggerOpeningEffect()
            },
            showHotspotDetails(hotspotDetails) {
                this.hotspotDetails = {
                    id: hotspotDetails.id,
                    time: this.getEventFormattedDateTimeFirst(hotspotDetails.time),
                    headline: hotspotDetails.headline,
                    location: hotspotDetails.location,
                    summary: hotspotDetails.summary,
                    threatLevel: hotspotDetails.threatLevel,
                    type: hotspotDetails.type,
                    latlng: JSON.parse(hotspotDetails.latlng),
                }
                this.currentActiveEvent = 'hotspots'
                this.showPanel()
                this.triggerOpeningEffect(this.hotspotDetails.latlng, 4)
                this.hotspotDetails.isLoading = true
                RiskIntelligenceAPIService.getHotspotThreatsAssessment(hotspotDetails.id).then(response => {
                    if (response.id) {
                        this.hotspotDetails.threatAssessment = response
                    }
                }).catch(error => {
                    Sentry.captureException(error)
                }).finally(() => {
                    this.hotspotDetails.isLoading = false
                })
            },
            triggerOpeningEffect(lngLat, zoomLevel = 8) {
                zoomLevel = MapBox.map.getZoom() < zoomLevel ? zoomLevel : MapBox.map.getZoom()
                window.setTimeout(() => {
                    if (lngLat !== undefined) {
                        if (lngLat.length > 0) {
                            MapBox.map.jumpTo({
                                center: lngLat,
                                zoom: zoomLevel
                            })
                        } else {
                            MapBox.map.jumpTo({
                                center: [lngLat.lng, lngLat.lat],
                                zoom: zoomLevel
                            })
                        }
                    }

                    this.$nextTick(() => {
                        MapBox.map.easeTo({
                            padding: {
                                // Would be nice to refactor it with Vue JS $refs
                                'right': document.querySelector('#vue-event-detail-panel .is-active').getClientRects()[0].width
                            },
                            duration: 0 // duration: 1000  @Adam I have just disabled for now as it is jittery
                        })
                    })
                })
            },
            triggerClosingEffect(lngLat, zoomLevel = 5) {
                MapBox.map.easeTo({
                    padding: { 'right': 0 },
                    duration: 0 // @Adam I have just disabled for now as it is jittery
                })

                if (lngLat) {
                    MapBox.map.flyTo({ center: lngLat, zoom: zoomLevel })
                } else {
                    MapBox.map.setZoom(zoomLevel)
                }
            },
            showPanel() {
                this.isPanelOpen = true
                document.getElementsByClassName('app-template')[0].classList.add('event-detail-panel-show')

                // Reset scroll position
                this.$refs.internationalSOSAlertContent.scrollTop = 0
                this.$refs.aviationAlertContent.scrollTop = 0
            },
            hidePanel() {
                this.isPanelOpen = false
                document.getElementsByClassName('app-template')[0].classList.remove('event-detail-panel-show')
                unhighlightHotspot()
                if (this.currentActiveEvent === 'avsec-alert') {
                    unhighlightCountry()
                    if (this.aviationAlertType === 'location') {
                        let latLngData = null
                        if (this.aviationAlerts[0]?.AffectedLocation?.latlng) {
                            latLngData = this.aviationAlerts[0].AffectedLocation?.latlng
                        } else {
                            if (this.aviationAlerts[0]?.AffectedLocation?.Coordinates?.length > 0) {
                                latLngData = {
                                    lat: this.aviationAlerts[0].AffectedLocation?.Coordinates[1],
                                    lng: this.aviationAlerts[0].AffectedLocation?.Coordinates[0]
                                }
                            }
                        }
                        if (latLngData) {
                            this.triggerClosingEffect([latLngData.lng, latLngData.lat], 7)
                        } else {
                            this.triggerClosingEffect(undefined, 7)
                        }
                    } else {
                        this.triggerClosingEffect(undefined, 7)
                    }

                    if (MapBox.map.getLayer('avsec-single-alert') !== undefined) {
                        MapBox.map.setLayoutProperty('avsec-single-alert', 'visibility', 'none')
                    }
                } else if (this.currentActiveEvent === 'international-sos-alert') {
                    this.triggerClosingEffect(this.internationalSOSAlertDetails.lngLat)

                    if (MapBox.map.getLayer('international-sos-single-alert') !== undefined) {
                        MapBox.map.setLayoutProperty('international-sos-single-alert', 'visibility', 'none')
                    }
                }
            },
            getEventFormattedDate(date) {
                return moment(date).format('DD MMM YYYY HH:mm') + ' ' + guessedTimeZone
            },
            getEventFormattedDateTimeFirst(date) {
                return moment(date).format('HH:mm DD MMM YYYY').toUpperCase() + ' ' + guessedTimeZone
            },
            getEventSimplifiedDate(date) {
                if (!date) return ''
                return moment(date).format('YYYY-MM-DD HH:mm') + ' ' + guessedTimeZone
            },
            getEventSimplifiedDateWithoutTimeZone(date) {
                if (!date) return ''
                return moment(date).format('YYYY-MM-DD HH:mm')
            },
            getEventJustDate(date) {
                if (!date) return ''
                return moment(date).format('DD MMM YYYY') + ' ' + guessedTimeZone
            },
            getHtml(description) {
                if (description) {
                    description = description.replace(/\r\n/g, '<br/>')
                    return description
                } else {
                    return ''
                }
            },
            toggleAccordion(event, element) {
                let accordion = null

                if (element !== undefined) {
                    accordion = element
                } else {
                    accordion = event.target.parentElement
                }

                let contentContainer = accordion.getElementsByClassName('accordion__content-container')[0]
                let contentHeight = accordion.getElementsByClassName('accordion__content')[0].offsetHeight
                if (accordion.classList.contains('show-content')) {
                    accordion.classList.remove('show-content')
                    contentContainer.setAttribute('style', 'height: 0')
                } else {
                    accordion.classList.add('show-content')
                    contentContainer.setAttribute('style', 'height:' + (parseInt(contentHeight) + 16) + 'px')
                }
            },
            updateAccordionHeights() {
                setTimeout(() => {
                    const accordionContainer = document.getElementsByClassName('show-content')
                    if (accordionContainer.length > 0) {
                        for (let index = 0; index < accordionContainer.length; index++) {
                            const contentContainer = accordionContainer[index].getElementsByClassName('accordion__content-container')[0]
                            const contentHeight = accordionContainer[index].getElementsByClassName('accordion__content')[0].offsetHeight
                            contentContainer.setAttribute('style', 'height:' + (parseInt(contentHeight) + 16) + 'px')
                        }
                    }
                }, 250)
            },
            exportHotspotDetails() {
                this.isExportingHotspotDetails = true
                this.hotspotDetails.DateOfDownload = moment().format('DD/MM/YYYY')

                ClientPortalAPIService.downloadBriefBuilderReport('hotspot', JSON.stringify(this.hotspotDetails)).then(res => {
                    try {
                        if (res) {
                            const pdfBlob = new Blob([res], { type: 'application/pdf' })

                            // Create a URL for the Blob
                            const blobUrl = URL.createObjectURL(pdfBlob)

                            // Create a link and simulate clicking to trigger the download
                            const link = document.createElement('a')
                            link.href = blobUrl
                            link.download = `Hotspot-Report-${this.hotspotDetails.headline}.pdf`
                            link.click()

                            // Clean up the Blob URL
                            URL.revokeObjectURL(blobUrl)
                        }
                    } catch (error) {
                        console.log('download hotspot report server error', error)
                        Sentry.captureException(error)
                    }
                }).catch(error => {
                    console.log('download hotspot report server error', error)
                    Sentry.captureException(error)
                }).finally(() => {
                    this.isExportingHotspotDetails = false
                })
            },
            processHtmlContent(htmlString) {
                if (!htmlString) {
                    return ''
                }
                try {
                    const parser = new DOMParser()
                    const doc = parser.parseFromString(htmlString, 'text/html')
                    doc.querySelectorAll('img').forEach(img => {
                        const src = img.getAttribute('src')
                        const a = document.createElement('a')
                        a.setAttribute('href', src)
                        a.setAttribute('target', '_blank')
                        img.parentNode.insertBefore(a, img)
                        a.appendChild(img)
                    })
                    return doc.body.innerHTML
                } catch (error) {
                    console.log('processHtmlContent error', error)
                    Sentry.captureException(error)
                    return htmlString
                }
            }
        },
        template: HTMLTemplate
    }

    const DOMElement = document.getElementById('vue-event-detail-panel')

    if (DOMElement === null) {
        return null
    } else {
        return Vue.createApp(vueComponent).mount(DOMElement)
    }
})()
